"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Search, MapPin, TrendingUp, ChartBar as BarChart3, ArrowRight, CircleCheck as CheckCircle2, Target, Globe, Building2, Sparkles, Zap, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
const gloucesterKeywords = [{
  term: "SEO consultant Gloucester",
  volume: "720/mo",
  competition: "Medium",
  opportunity: "High (Direct Intent)",
  intent: "Commercial / Hire"
}, {
  term: "Local SEO services Gloucestershire",
  volume: "1,100/mo",
  competition: "Medium-High",
  opportunity: "High (High Value)",
  intent: "Commercial"
}, {
  term: "Digital marketing consultancy Gloucester",
  volume: "890/mo",
  competition: "Medium",
  opportunity: "Very High",
  intent: "B2B Lead"
}, {
  term: "Commercial solicitors Gloucester",
  volume: "1,400/mo",
  competition: "High",
  opportunity: "Map Pack Focus",
  intent: "Local B2B"
}, {
  term: "Industrial logistics hub Gloucester",
  volume: "590/mo",
  competition: "Low-Medium",
  opportunity: "Niche Authority",
  intent: "Informational & Lead"
}];
const neighboringLocations = [{
  name: "Local SEO Overview",
  path: "/LocalSEOPage",
  region: "National Strategy"
}, {
  name: "Wolverhampton SEO",
  path: "/LocalSeoConsultantWolverhamptonPage",
  region: "West Midlands"
}, {
  name: "Northampton SEO",
  path: "/LocalSeoConsultantNorthamptonPage",
  region: "East Midlands"
}, {
  name: "Milton Keynes SEO",
  path: "/LocalSeoConsultantMiltonKeynesPage",
  region: "South East / Bucks"
}, {
  name: "Derby SEO Consultancy",
  path: "/LocalSeoConsultantDerbyPage",
  region: "East Midlands"
}, {
  name: "Stoke-on-Trent SEO",
  path: "/LocalSeoConsultantStokeOnTrentPage",
  region: "Staffordshire"
}, {
  name: "Wakefield Local SEO",
  path: "/LocalSeoConsultantWakefieldPage",
  region: "Yorkshire"
}, {
  name: "Peterborough SEO",
  path: "/LocalSeoConsultantPeterboroughPage",
  region: "Cambridgeshire"
}];
const opportunityPoints = [{
  title: "Gloucester Docks & Quays Commercial Boom",
  description: "Surge in local search intent for B2B and consumer services across the regenerated Docks and Quays corridor.",
  icon: Building2
}, {
  title: "Google Map Pack Dominance",
  description: "High volume of localized queries present an immediate opportunity for optimized Google Business Profiles and local citations.",
  icon: MapPin
}, {
  title: "M5 Corridor Logistics & Trade Reach",
  description: "Logistics and engineering sectors in Gloucestershire require targeted geo-targeted B2B campaigns to capture regional contracts.",
  icon: Target
}, {
  title: "AI Search Readiness",
  description: "Preparing Gloucester businesses for Google Gemini and AI Overviews through direct entity-based schema and structured data.",
  icon: Sparkles
}];
export default function Gloucester() {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const staggerContainer = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  return <section id="gloucesterlocalseoanalysis" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-t border-border overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="text-center max-w-3xl mx-auto mb-16">
          <Badge variant="secondary" className="mb-4 gap-1.5 px-3 py-1 text-xs sm:text-sm font-medium">
            <MapPin className="w-3.5 h-3.5 text-primary" />
            Gloucester Local SEO Analysis &amp; Search Insights
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default">
            Unlocking Search Growth Across Gloucester &amp; Gloucestershire
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            I carry out detailed, hands-on search market research to help businesses in Gloucester outrank regional competition. Here is a direct breakdown of real local search intent, keyword opportunities, and strategic pathways.
          </p>
        </motion.div>

        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          <motion.div variants={fadeIn} data-index={0}>
            <Card className="bg-card border-border shadow-xs hover:border-primary/40 transition-[border-color,box-shadow]">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground font-default">
                  Gloucester Local Search Demand
                </CardTitle>
                <TrendingUp className="w-4 h-4 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-foreground font-default">+28% YoY</div>
                <p className="text-xs text-muted-foreground mt-1 font-default">
                  Steady increase in local service queries across GL1-GL4 postcodes.
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={fadeIn} data-index={1}>
            <Card className="bg-card border-border shadow-xs hover:border-primary/40 transition-[border-color,box-shadow]">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground font-default">
                  Map Pack Competition
                </CardTitle>
                <BarChart3 className="w-4 h-4 text-accent" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-foreground font-default">Moderate</div>
                <p className="text-xs text-muted-foreground mt-1 font-default">
                  Significant opportunity to win top 3 map placements with optimization.
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={fadeIn} data-index={2}>
            <Card className="bg-card border-border shadow-xs hover:border-primary/40 transition-[border-color,box-shadow]">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground font-default">
                  B2B Lead Intent
                </CardTitle>
                <Zap className="w-4 h-4 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-foreground font-default">64% Commercial</div>
                <p className="text-xs text-muted-foreground mt-1 font-default">
                  High proportion of searches carry immediate buying or hiring intent.
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={fadeIn} data-index={3}>
            <Card className="bg-card border-border shadow-xs hover:border-primary/40 transition-[border-color,box-shadow]">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground font-default">
                  Direct Consultant Support
                </CardTitle>
                <ShieldCheck className="w-4 h-4 text-accent" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-foreground font-default">100% Direct</div>
                <p className="text-xs text-muted-foreground mt-1 font-default">
                  You work directly with Yassine local strategy, zero account managers.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="mb-16">
          <Card className="bg-card border-border shadow-sm">
            <CardHeader className="border-b border-border bg-muted/30">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-xl font-semibold text-foreground font-default flex items-center gap-2">
                    <Search className="w-5 h-5 text-primary" />
                    Gloucester High-Intent Keyword Matrix
                  </CardTitle>
                  <CardDescription className="text-muted-foreground font-default mt-1">
                    Sample search term analysis highlighting key conversion pathways for local Gloucester businesses.
                  </CardDescription>
                </div>
                <Badge variant="outline" className="w-fit text-xs border-primary/30 text-primary">
                  Updated Search Data
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader className="bg-muted/50">
                  <TableRow>
                    <TableHead className="font-semibold text-foreground">Target Search Query</TableHead>
                    <TableHead className="font-semibold text-foreground">Estimated Search Volume</TableHead>
                    <TableHead className="font-semibold text-foreground">Competition Score</TableHead>
                    <TableHead className="font-semibold text-foreground">Opportunity Rating</TableHead>
                    <TableHead className="font-semibold text-foreground">Primary Search Intent</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {gloucesterKeywords.map((row, idx) => <TableRow key={idx} data-index={idx} className="hover:bg-muted/30 transition-colors">
                      <TableCell className="font-medium text-foreground">{row.term}</TableCell>
                      <TableCell className="text-muted-foreground">{row.volume}</TableCell>
                      <TableCell>
                        <Badge variant={row.competition.includes("High") ? "destructive" : "secondary"} className="text-xs">
                          {row.competition}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium text-primary">{row.opportunity}</TableCell>
                      <TableCell className="text-muted-foreground">{row.intent}</TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="lg:col-span-1 flex flex-col justify-between bg-primary text-primary-foreground p-8 rounded-xl shadow-md">
            <div>
              <Badge variant="secondary" className="bg-primary-foreground/10 text-primary-foreground border-none mb-4">
                Personal Consultation Strategy
              </Badge>
              <h3 className="text-2xl font-bold font-default mb-4">
                Why My Independent SEO Approach Works in Gloucester
              </h3>
              <p className="text-primary-foreground/90 font-default text-sm leading-relaxed mb-6">
                Agencies often reuse generic local templates across UK cities. When you work with me, I build a custom Gloucester search blueprint designed directly for your business goals, target postcodes, and competitive environment.
              </p>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-accent shrink-0" />
                  <span>Direct consultant contact (Yassine)</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-accent shrink-0" />
                  <span>Tailored technical &amp; content audit</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-accent shrink-0" />
                  <span>Transparent reporting &amp; ROI tracking</span>
                </li>
              </ul>
            </div>
            <div className="mt-8 pt-6 border-t border-primary-foreground/20">
              <Button asChild size="lg" className="w-full bg-accent text-accent-foreground hover:bg-accent/90">
                <Link to="/ContactPage">
                  Book Your Free Consultation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {opportunityPoints.map((point, i) => {
            const Icon = point.icon;
            return <Card key={i} data-index={i} className="bg-card border-border shadow-xs hover:border-primary/40 transition-[border-color,box-shadow]">
                  <CardHeader>
                    <div className="p-2.5 w-fit rounded-lg bg-secondary text-secondary-foreground mb-3">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <CardTitle className="text-lg font-semibold text-foreground font-default">
                      {point.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground font-default leading-relaxed">
                      {point.description}
                    </p>
                  </CardContent>
                </Card>;
          })}
          </motion.div>
        </div>

        <Separator className="my-12" />

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="mb-12">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <h3 className="text-xl font-semibold text-foreground font-default flex items-center justify-center gap-2">
              <Globe className="w-5 h-5 text-primary" />
              Regional &amp; National Local SEO Coverage
            </h3>
            <p className="text-sm text-muted-foreground font-default mt-2">
              Explore my tailored local SEO strategies across neighboring markets and major economic hubs in the UK.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {neighboringLocations.map((loc, idx) => <Card key={idx} data-index={idx} className="bg-card border-border shadow-xs hover:border-primary/50 transition-[border-color,box-shadow] p-4 text-center group">
                <Link to={loc.path} className="block text-decoration-none">
                  <div className="text-xs text-muted-foreground font-default mb-1">{loc.region}</div>
                  <div className="text-sm font-medium text-foreground group-hover:text-primary transition-colors flex items-center justify-center gap-1">
                    {loc.name}
                    <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </Link>
              </Card>)}
          </div>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="bg-muted/50 border border-border rounded-2xl p-8 sm:p-10 text-center max-w-4xl mx-auto shadow-xs">
          <h3 className="text-2xl sm:text-3xl font-bold text-foreground font-default mb-3">
            Ready to Accelerate Your Gloucester Organic Search Growth?
          </h3>
          <p className="text-muted-foreground text-sm sm:text-base font-default max-w-2xl mx-auto mb-8">
            Get an in-depth audit of your current local search standings, map pack positions, and keyword targets with direct insights from me.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/CaseStudiesPage">
                Explore Genuine Case Studies
              </Link>
            </Button>
          </div>
        </motion.div>

      </div>
    </section>;
}